import { useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { Eye, WifiOff } from "lucide-react";
import { docName } from "@hydra-notes/shared";
import { resolveItem, useItem, useSpaceOf } from "@/data/store";
import { useFolderMeta } from "@/data/sharedFolders";
import { useSyncState } from "@/data/sync";
import { Editor } from "@/editor/Editor";
import { editorSpace } from "@/editor/space";
import { registerEditor } from "@/editor/registry";
import { openByTitle } from "@/commands/nav";
import type { NoteHandle } from "@/data/notes";
import { useDocPresence } from "@/data/presence";
import { PresenceChips } from "@/ui/PresenceChips";
import s from "./NoteView.module.css";

/**
 * A note: inline editable title + the live-preview editor bound to the
 * note's Y.Text. Content edits bump the workspace item's updatedAt
 * (debounced) which drives search reindexing and "edited" metadata.
 *
 * Shared notes (space kind "shared") additionally flip read-only for the
 * viewer role and while disconnected: they are online-only, so an offline
 * edit would have nowhere to land.
 */
export function NoteView({ noteId }: { noteId: string }) {
  const item = useItem(noteId);
  const space = useSpaceOf(noteId);
  const isShared = space?.kind === "shared";
  const folderMeta = useFolderMeta(isShared ? space?.spaceId : null);
  const syncStatus = useSyncState((st) => st.status);
  const [handle, setHandle] = useState<NoteHandle | null>(null);
  const touchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const spaceId = space?.spaceId;
  const present = useDocPresence(spaceId, spaceId ? docName.note(spaceId, noteId) : undefined);
  useEffect(() => {
    if (!spaceId) return;
    const res = resolveItem(noteId);
    if (!res) return;
    const ctx = res.ctx;
    const h = ctx.notes.open(noteId);
    let alive = true;
    void h.whenLoaded.then(() => {
      if (alive) setHandle(h);
    });

    const onUpdate = (_u: Uint8Array, _origin: unknown, _doc: Y.Doc, tr: Y.Transaction) => {
      // Only local edits bump updatedAt; persistence loads and remote sync
      // echoes arrive as non-local transactions.
      if (!tr.local) return;
      if (touchTimer.current) clearTimeout(touchTimer.current);
      touchTimer.current = setTimeout(() => {
        ctx.workspace.touch(noteId);
      }, 1200);
    };
    h.doc.on("update", onUpdate);

    return () => {
      alive = false;
      h.doc.off("update", onUpdate);
      if (touchTimer.current) clearTimeout(touchTimer.current);
      h.release();
      setHandle(null);
    };
  }, [noteId, spaceId]);

  const yjs = useMemo(() => {
    if (!handle || !spaceId) return undefined;
    const res = resolveItem(noteId);
    if (!res) return undefined;
    return {
      text: handle.text,
      undoManager: new Y.UndoManager(handle.text, { captureTimeout: 400 }),
      awareness: res.ctx.sync?.getAwareness(docName.note(spaceId, noteId)) ?? undefined,
    };
  }, [handle, noteId, spaceId]);

  const extra = useMemo(
    () => (spaceId ? [editorSpace.of({ spaceId, shared: isShared, noteId })] : []),
    [spaceId, isShared, noteId],
  );

  if (!item || !space) return <div className={s.loading}>Note not found</div>;

  const isViewer = isShared && folderMeta?.role === "viewer";
  const disconnected = isShared && syncStatus !== "synced" && syncStatus !== "syncing";
  const readOnly = isViewer || disconnected || (isShared && !!folderMeta?.locked);

  return (
    <div className={s.view}>
      {isViewer ? (
        <div className={s.banner}>
          <Eye size={13} /> Read only: you have viewer access in "{folderMeta?.title}"
        </div>
      ) : disconnected ? (
        <div className={s.banner}>
          <WifiOff size={13} /> Reconnecting... editing shared notes is paused while offline
        </div>
      ) : null}
      <div className={s.titleWrap}>
        <input
          className={s.title}
          placeholder="Untitled"
          defaultValue={item.title}
          autoFocus={!item.title && !readOnly}
          readOnly={readOnly}
          key={`${noteId}:${item.title === "" ? "empty" : "t"}`}
          onBlur={(e) => {
            if (readOnly) return;
            const res = resolveItem(noteId);
            if (res && e.target.value !== item.title) res.ctx.workspace.rename(noteId, e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "ArrowDown") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
              document.querySelector<HTMLElement>(".cm-content")?.focus();
            }
          }}
        />
        <div className={s.meta}>
          <PresenceChips present={present} verb="is reading" writingVerb="is writing" />
          <span>Created {fmtDate(item.createdAt)}</span>
          <span>Edited {fmtDate(item.updatedAt)}</span>
        </div>
      </div>
      {handle && yjs ? (
        <Editor
          viewKey={noteId}
          yjs={yjs}
          className={s.editor}
          readOnly={readOnly}
          extra={extra}
          callbacks={{
            onWikilinkClick: (target) => openByTitle(target, spaceId),
          }}
          onViewReady={(view) => registerEditor(noteId, view)}
        />
      ) : (
        <div className={s.loading}>{isShared && syncStatus === "offline" ? "This shared note needs a connection" : "Loading…"}</div>
      )}
    </div>
  );
}

function fmtDate(ts: number): string {
  if (!ts) return "–";
  const d = new Date(ts);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
